import { useEffect } from 'react';

// SaveFlash — the platform's transient save-confirmation banner (s72 owner UX standard).
// Purely CLIENT-SIDE and ephemeral by design: it renders once, immediately after the save
// action that set `message`, auto-clears after `ms`, and cannot reappear on refresh or on
// another device because nothing is persisted anywhere. (Contrast with the client documents
// glow, which is notification-backed server state — a save confirmation must NOT persist,
// so no server round trip exists to replay it.)
//
// Usage:
//   const [flash, setFlash] = useState<string | null>(null);
//   ... after a successful save: setFlash('Draft saved');
//   <SaveFlash message={flash} onClear={() => setFlash(null)} />

export interface SaveFlashProps {
  /** The confirmation text, or null to render nothing. */
  message: string | null;
  /** Called when the flash times out (clear the state that set it). */
  onClear: () => void;
  /** Visible duration in milliseconds (default 3000). */
  ms?: number;
}

export function SaveFlash({ message, onClear, ms = 3000 }: SaveFlashProps): JSX.Element | null {
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(onClear, ms);
    return () => clearTimeout(t);
  }, [message, ms, onClear]);

  if (!message) return null;
  return (
    <div className="rounded-md border border-[var(--tint-green-border)] bg-[var(--tint-green-bg-soft)] px-3 py-2 text-sm text-[var(--tint-green-text)]">
      {message}
    </div>
  );
}
